import React, { useEffect, useRef, useState } from "react";
import {
  useVoiceWidget,
  WidgetConfig,
  VoiceWidgetConfig,
  ThemeConfig,
} from "../hooks/useVoiceWidget";
import { getThemeValue } from "../hooks/theme";
import VoiceButton from "./VoiceButton";
import AvatarFloatingButton from "./AvatarFloatingButton";
import ChatWindow from "./ChatWindow";
import ErrorToast from "./ErrorToast";
import FloatingBookingCta from "./FloatingBookingCta";
import { useWidgetStore } from "../store";
import { serializeUserError } from "../utils/errorMessages";
import { getInstallationStatus } from "../services/api";
import { getStrings, Language } from "../i18n/strings";
import { resolveFloatingCornerStyle } from "../utils/floatingPosition";

interface RemstalVoiceWidgetProps {
  config?: WidgetConfig; // For backward compatibility
  agentId?: string; // For multi-widget setups
  widgetId?: string; // For multi-widget setups  
  mode?: "voice" | "chat" | "combined";
  onError?: (error: string) => void;
  onStateChange?: (state: { connected: boolean; recording: boolean; isAgentSpeaking: boolean }) => void;
  onWidgetReady?: (triggerFn: () => void) => void;
  /**
   * When false, skip GET /api/widget/config and paint from embed config immediately.
   * Admin Live preview only. Default true (production).
   */
  loadPublicConfig?: boolean;
}

const RemstalVoiceWidget: React.FC<RemstalVoiceWidgetProps> = ({
  config,
  agentId,
  widgetId,
  mode = "combined",
  onError,
  onStateChange,
  onWidgetReady,
  loadPublicConfig = true,
}) => {
  // Build the actual config for the hook
  const hookConfig: WidgetConfig | VoiceWidgetConfig = config || {
    agentId: agentId!,
    widgetId: widgetId!,
    mode: mode,
  };
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs для хранения latest версий callback'ов, чтобы избежать infinite loop
  const onStateChangeRef = useRef(onStateChange);
  const onWidgetReadyRef = useRef(onWidgetReady);
  const onErrorRef = useRef(onError);

  const {
    buttonState,
    connectionStatus,
    isConnected,
    isRecording,
    isAgentSpeaking,
    error,
    config: widgetConfig,
    shellDecided,
    messages,
    transcript,
    conversationItems,
    hasQuota,
    quotaCheckComplete,
    widgetName,
    welcomeMessage,
    isAgentThinking,
    handleButtonClick,
    sendMessage,
    markUserActivity,
    destroy,
  } = useVoiceWidget(hookConfig, { loadPublicConfig });

  const isEmbedded = !!(config?.embed);

  // Синхронизируем refs с props (без добавления в dependency array основных useEffect'ов)
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    onWidgetReadyRef.current = onWidgetReady;
  }, [onWidgetReady]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (error && onErrorRef.current) {
      // Preserve the legacy embedder contract: pass a flat string.
      onErrorRef.current(serializeUserError(error));
    }
  }, [error]);

  useEffect(() => {
    if (onStateChangeRef.current) {
      onStateChangeRef.current({ connected: isConnected, recording: isRecording, isAgentSpeaking });
    }
  }, [isConnected, isRecording, isAgentSpeaking]);

  useEffect(() => {
    if (onWidgetReadyRef.current && handleButtonClick) {
      // Pass the trigger function only - cleanup will happen via unmount
      onWidgetReadyRef.current(handleButtonClick);
    }
  }, [handleButtonClick]);

  useEffect(() => {
    if (containerRef.current && widgetConfig.theme) {
      applyThemeToContainer(containerRef.current, widgetConfig.theme);
    }
  }, [widgetConfig.theme]);

  useEffect(() => {
    if (quotaCheckComplete && hasQuota) {
      setIsVisible(true);
    } else if (quotaCheckComplete && !hasQuota) {
      setIsVisible(false);
    }
  }, [quotaCheckComplete, hasQuota]);

  const applyThemeToContainer = (
    container: HTMLElement,
    theme: Partial<ThemeConfig>
  ) => {
    if (theme.primaryColor) {
      container.style.setProperty("--theme-primary", theme.primaryColor);
    }
    if (theme.fontFamily) {
      container.style.setProperty("--theme-font", theme.fontFamily);
    }
    if (theme.fontSize) {
      container.style.setProperty("--theme-font-size", theme.fontSize);
    }
    if (theme.borderRadius) {
      container.style.setProperty("--theme-border-radius", theme.borderRadius);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Additional key handling if needed
  };

  const getContainerClassName = () => {
    const baseClass = "remstal-voice-widget";
    if (isEmbedded) {
      return `${baseClass} remstal-voice-widget-embedded`;
    }
    return `${baseClass} rv-position-${widgetConfig.position}`;
  };

  /** Sole viewport position writer — children stay relative/absolute in-shell. */
  const getContainerStyle = (): React.CSSProperties => {
    if (isEmbedded) {
      return { overflow: "visible" };
    }
    return {
      ...resolveFloatingCornerStyle(
        widgetConfig.position || "bottom-right",
        widgetConfig.offset || { x: 20, y: 20 }
      ),
      // Pulse rings / booking CTA may extend outside the launcher box.
      overflow: "visible",
    };
  };

  const getButtonContent = () => {
    if (isEmbedded && config.embed?.buttonText) {
      return config.embed.buttonText;
    }
    return undefined;
  };

  const getButtonStyle = (): React.CSSProperties => {
    if (isEmbedded && config.embed?.buttonStyles) {
      return config.embed.buttonStyles;
    }
    return {};
  };

  const avatarButtonSize = parseInt(getThemeValue(widgetConfig.theme, "buttonSize"), 10) || 96;
  const latestBookingSuggestion = [...conversationItems]
    .reverse()
    .find(
      (item) =>
        item.type === "suggestion" &&
        item.suggestion?.type === "booking" &&
        !!item.suggestion.action_url
    )?.suggestion;

  const clearError = useWidgetStore((s) => s.clearError);

  if (!isVisible) {
    // When the widget is hidden (quota zero) we still surface errors so the
    // user understands why nothing is happening.
    return <ErrorToast error={error} onDismiss={clearError} />;
  }

  // One readiness gate: no mode-sensitive chrome until shell is decided.
  if (!shellDecided) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={getContainerClassName()}
      style={getContainerStyle()}
      data-widget="remstal-voice"
    >
      <ErrorToast error={error} onDismiss={clearError} />
      {widgetConfig.mode === "voice" ? (
        widgetConfig.avatarUrl ? (
          <AvatarFloatingButton
            agentId={widgetConfig.agentId!}
            widgetId={widgetConfig.widgetId || widgetConfig.agentId!}
            avatarUrl={widgetConfig.avatarUrl}
            name={widgetName || getStrings(widgetConfig.language as Language).fallbackName}
            position={widgetConfig.position === "custom" ? "bottom-right" : widgetConfig.position}
            size={avatarButtonSize}
            onClick={handleButtonClick}
            disabled={buttonState === "disabled"}
            mode={widgetConfig.mode}
            theme={widgetConfig.theme}
          />
        ) : (
          <VoiceButton
            state={buttonState}
            onClick={handleButtonClick}
            onKeyDown={handleKeyDown}
            isEmbedded={isEmbedded}
            style={getButtonStyle()}
            theme={widgetConfig.theme}
            language={widgetConfig.language as Language}
          >
            {getButtonContent()}
          </VoiceButton>
        )
      ) : null}

      {widgetConfig.mode === "voice" && latestBookingSuggestion ? (
        <FloatingBookingCta
          suggestion={latestBookingSuggestion}
          buttonSize={avatarButtonSize}
          position={widgetConfig.position === "custom" ? "bottom-right" : widgetConfig.position}
          theme={widgetConfig.theme}
        />
      ) : null}

      {widgetConfig.mode === "chat" || widgetConfig.mode === "combined" ? (
        <ChatWindow
          messages={messages}
          transcript={transcript}
          conversationItems={conversationItems}
          onSendMessage={sendMessage}
          onUserActivity={markUserActivity}
          mode={widgetConfig.mode}
          isConnected={isConnected}
          isRecording={isRecording}
          isAgentSpeaking={isAgentSpeaking}
          isAgentThinking={isAgentThinking}
          buttonState={buttonState}
          onVoiceButtonClick={handleButtonClick}
          onStopConversation={destroy}
          isEmbedded={isEmbedded}
          theme={widgetConfig.theme}
          uiVariant={widgetConfig.uiVariant}
          linkButtonsEnabled={widgetConfig.linkButtonsEnabled}
          widgetName={widgetName}
          welcomeMessage={welcomeMessage}
          avatarUrl={widgetConfig.avatarUrl}
          language={widgetConfig.language as Language}
        />
      ) : null}
    </div>
  );
};

export default RemstalVoiceWidget;
